import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { getBuraxilisQuestionsForFenn } from '../../utils/buraxilisQuestionUtils';
import { blankCtxFromExam } from '../../constants/buraxilisConfig';
import { getSelectedExam } from '../../services/examService';
import BuraxilisQuestionCheckboxGroup from './BuraxilisQuestionCheckboxGroup';
import PrefixFilterPanel from './PrefixFilterPanel';
import { SINIFLAR } from '../../constants/fennConfig';
import type { PrefixInfo } from '../../utils/studentIdPrefixUtils';
import type { StudentIdPrefixFilter } from '../../types';

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  input {
    cursor: pointer;
  }
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BURAXILIS_FENNS = ['Azərbaycan dili', 'Riyaziyyat', 'İngilis dili', 'Rus dili', 'Alman dili', 'Fransız dili'];
const VARIANTS = ['A', 'B', 'C', 'D'];

export interface BuraxilisAssignmentFormProps {
  sinif: string;
  variant: string;
  selectedFenn: string;
  fennOptions: string[];
  onSinifChange: (v: string) => void;
  onVariantChange: (v: string) => void;
  onFennChange: (v: string) => void;
  onSubmit: (payload: { questionNumbers: string[]; studentIdPrefixFilter?: StudentIdPrefixFilter }) => void;
  disabled?: boolean;
  loading?: boolean;
  onFetchPrefixes?: (sinif: string, variant: string, fenn: string) => Promise<PrefixInfo[]>;
}

const BuraxilisAssignmentForm: React.FC<BuraxilisAssignmentFormProps> = ({
  sinif,
  variant,
  selectedFenn,
  fennOptions,
  onSinifChange,
  onVariantChange,
  onFennChange,
  onSubmit,
  disabled = false,
  loading = false,
  onFetchPrefixes,
}) => {
  const [assignmentMode, setAssignmentMode] = useState<'all' | 'split'>('all');
  const [selectedQuestionNumbers, setSelectedQuestionNumbers] = useState<string[]>([]);
  const [prefixFilter, setPrefixFilter] = useState<StudentIdPrefixFilter | undefined>(undefined);
  const [availablePrefixes, setAvailablePrefixes] = useState<PrefixInfo[]>([]);
  const [prefixLoading, setPrefixLoading] = useState(false);

  const availableQuestions = useMemo(() => {
    if (!sinif || !selectedFenn) return [];
    return getBuraxilisQuestionsForFenn(
      Number(sinif),
      selectedFenn,
      blankCtxFromExam(getSelectedExam())
    );
  }, [sinif, selectedFenn]);

  const effectiveFennOptions = fennOptions.length > 0 ? fennOptions : BURAXILIS_FENNS;

  // Fetch available prefixes when sinif+variant+fenn are all set
  const stableFetchPrefixes = useCallback(
    (s: string, v: string, f: string) => onFetchPrefixes?.(s, v, f),
    [onFetchPrefixes]
  );

  useEffect(() => {
    if (sinif && variant && selectedFenn && onFetchPrefixes) {
      setPrefixLoading(true);
      stableFetchPrefixes(sinif, variant, selectedFenn)
        ?.then(setAvailablePrefixes)
        .catch(() => setAvailablePrefixes([]))
        .finally(() => setPrefixLoading(false));
    } else {
      setAvailablePrefixes([]);
      setPrefixFilter(undefined);
    }
  }, [sinif, variant, selectedFenn, stableFetchPrefixes, onFetchPrefixes]);

  const handleSubmit = () => {
    if (!sinif) {
      alert('Lütfən sinif seçin');
      return;
    }
    if (!variant) {
      alert('Lütfən variant seçin');
      return;
    }
    if (!selectedFenn) {
      alert('Lütfən fənn seçin');
      return;
    }

    let questionNumbers: string[];
    if (assignmentMode === 'all') {
      questionNumbers = availableQuestions;
      if (!questionNumbers || questionNumbers.length === 0) {
        alert(`${sinif}-ci sinif üçün ${selectedFenn} fənnində sual tapılmadı`);
        return;
      }
    } else {
      if (selectedQuestionNumbers.length === 0) {
        alert('Sulları böl rejimində ən azı bir sual seçin');
        return;
      }
      questionNumbers = [...selectedQuestionNumbers].sort();
    }

    onSubmit({ questionNumbers, studentIdPrefixFilter: prefixFilter });
  };

  return (
    <Form>
      {effectiveFennOptions.length > 1 && (
        <Row style={{ marginBottom: '4px' }}>
          <Select
            value={selectedFenn}
            onChange={(e) => onFennChange(e.target.value)}
            disabled={disabled}
            style={{ gridColumn: '1 / -1', background: '#fef3c7', borderColor: '#f59e0b', fontWeight: 600 }}
          >
            <option value="">Fənn seçin</option>
            {effectiveFennOptions.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </Select>
        </Row>
      )}
      <Row>
        <Select value={sinif} onChange={(e) => onSinifChange(e.target.value)} disabled={disabled}>
          <option value="">Sinif seçin</option>
          {SINIFLAR.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <Select value={variant} onChange={(e) => onVariantChange(e.target.value)} disabled={disabled}>
          <option value="">Variant seçin</option>
          {VARIANTS.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </Select>
      </Row>
      <RadioGroup>
        <RadioLabel>
          <input
            type="radio"
            name="assignmentMode"
            checked={assignmentMode === 'all'}
            onChange={() => setAssignmentMode('all')}
            disabled={disabled}
          />
          Bütün suallar
        </RadioLabel>
        <RadioLabel>
          <input
            type="radio"
            name="assignmentMode"
            checked={assignmentMode === 'split'}
            onChange={() => setAssignmentMode('split')}
            disabled={disabled}
          />
          Sulları böl
        </RadioLabel>
      </RadioGroup>
      {assignmentMode === 'split' && sinif && selectedFenn && (
        <BuraxilisQuestionCheckboxGroup
          availableQuestions={availableQuestions}
          selectedQuestionNumbers={selectedQuestionNumbers}
          onChange={setSelectedQuestionNumbers}
          disabled={disabled}
        />
      )}
      {onFetchPrefixes && (
        <PrefixFilterPanel
          availablePrefixes={availablePrefixes}
          value={prefixFilter}
          onChange={setPrefixFilter}
          loading={prefixLoading}
          disabled={disabled}
          fieldsSelected={!!(sinif && variant && selectedFenn)}
        />
      )}
      <SubmitButton type="button" onClick={handleSubmit} disabled={disabled || loading}>
        {loading ? '...' : '➕ Təyin Et'}
      </SubmitButton>
    </Form>
  );
};

export default BuraxilisAssignmentForm;
